import { useCallback, useEffect } from 'react';
import {
  generatePath,
  matchPath,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import { z } from 'zod';

import { GetAllJobs } from '@/api/fe-api/view/queries/capacity-evalution/get-all-jobs';
import { FullPageContent } from '@/components/layout/full-page-content';
import {
  CapacityTestContent,
  CapacityTestSidebar,
} from '@/components/pages/assets/capacity-test';
import { codec, useQueryParams } from '@/hooks/router/use-query-params';

export function CapacityTestPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const jobList = GetAllJobs.useQuery({ siteId });

  const [{ id }] = useQueryParams({
    id: {
      key: 'id',
      schema: z.string().optional(),
      codec: codec.string,
    },
  });

  const location = useLocation();
  const history = useHistory();

  const navigateToTest = useCallback(
    (selectedId: string) =>
      history.push({
        pathname: generatePath('/asset/:siteId/capacity-test', { siteId }),
        search: new URLSearchParams({
          id: selectedId,
        }).toString(),
      }),
    [history, siteId],
  );

  const navigateToCreate = useCallback(
    () =>
      history.push(
        generatePath('/asset/:siteId/capacity-test/new', { siteId }),
      ),
    [history, siteId],
  );

  const navigateToContent = useCallback(() => {
    if (!jobList.data) {
      return;
    }

    const latestCompletedTest = jobList.data.filter(
      (item) => item.status === 'Completed',
    );

    if (latestCompletedTest.length > 0) {
      navigateToTest(latestCompletedTest[0].id);
      return;
    }

    navigateToCreate();
  }, [jobList.data, navigateToTest, navigateToCreate]);

  useEffect(() => {
    const isExactMatch = matchPath(location.pathname, {
      path: '/asset/:siteId/capacity-test',
      exact: true,
    });
    const hasNoQueryParams = !location.search;

    if (isExactMatch && hasNoQueryParams) {
      navigateToContent();
    }
  }, [location.pathname, location.search, navigateToContent]);

  return (
    <FullPageContent
      testId="capacity-test-page"
      scrollable={false}
      className="h-full"
    >
      <div className="flex h-full">
        <CapacityTestSidebar
          className="shrink-0 overflow-hidden border-r border-gray-200"
          selectedId={id}
          setSelectedId={navigateToTest}
        />
        <CapacityTestContent
          className="grow overflow-auto bg-zinc-50"
          selectedId={id}
        />
      </div>
    </FullPageContent>
  );
}
